/**
 * The primary action colour — the company's brand colour as a solid fill.
 *
 * Primary buttons wear the colour the company picked in Company settings
 * (served by `/branding`), exactly as picked, in both modes. Only the text
 * colour adapts: white or near-black, whichever reads better on that fill,
 * so a pale brand colour (a yellow, a mint) still gets legible labels.
 * Hover and edge shades are derived from it in `globals.css`.
 *
 * No company colour → no override: `--action` falls back to the design's
 * blue in `globals.css`.
 */

const HEX = /^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i
const INK = "#0b0c0f"
const WHITE = "#ffffff"

/** WCAG relative luminance of an sRGB hex. */
function luminance(hex: string): number {
  const raw = HEX.exec(hex.trim())?.[1] ?? "000"
  const full =
    raw.length === 3
      ? raw
          .split("")
          .map((c) => c + c)
          .join("")
      : raw.slice(0, 6)
  const [r, g, b] = [0, 2, 4].map((i) => {
    const v = parseInt(full.slice(i, i + 2), 16) / 255
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4
  }) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

const contrast = (a: number, b: number) =>
  (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05)

/** White or near-black — whichever has more contrast on `hex`. */
export function readableOn(hex: string): string {
  const l = luminance(hex)
  return contrast(l, 1) >= contrast(l, luminance(INK)) ? WHITE : INK
}

/** CSS for the action colour, or `null` to fall back to the default. */
export function actionCss(color: string | null | undefined): string | null {
  if (!color || !HEX.test(color.trim())) return null
  const fill = color.trim()
  const decl = `--action:${fill} !important;--on-action:${readableOn(fill)} !important;`
  // Every theme scope: the brand colour is the same in Day and Night.
  return `:root,.dark,[data-theme]{${decl}}`
}

const STYLE_EL_ID = "vx-action"

/** Apply (or clear) the action colour via one managed <style> tag. */
export function applyAction(color: string | null | undefined): void {
  if (typeof document === "undefined") return
  const css = actionCss(color)
  let el = document.getElementById(STYLE_EL_ID) as HTMLStyleElement | null
  if (!css) {
    el?.remove()
    return
  }
  if (!el) {
    el = document.createElement("style")
    el.id = STYLE_EL_ID
    document.head.appendChild(el)
  }
  el.textContent = css
}
